import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { HttpStateLine } from 'state-line';
import Container from 'xgimi-ui/Container';
import Button from 'xgimi-ui/Button';
import { getUrlParams } from 'xgimi-utils/url';
import App from '@xgimi-private/xgimi-app-bridge';
import { InfoSelectOption } from 'xgimi-ui/Info/Select';
import Upload from '@/components/Upload';
import Input from '@/components/Input';
import { isWechat } from '@/appUtils/isWechat';
import StoreComponent, { mapStateToProps } from '@/appComponents/Store';

import images from '@/assets/images';
import Api from '@/api';
import Type from './Type';
import Device from './Device';
import s from './.module.scss';

interface Props {
}

interface States {
  // 内容
  content: string;
  // 内容长度
  textLength: number;
  // 图片列表
  imageList: string[] | string ;
  // 打开选择类型弹框
  isType: boolean;
  // 反馈类型编码
  typeCode: string;
  // 反馈类型
  desc: string | null;
  // 问题类型
  typeList: Record<string, any>[];
  // 提交按钮loading
  ButtonLoading: boolean;
  // 是否打开设备弹框
  isOpenDevice: boolean;
  // 设备名字
  device: string;
  // 序列号
  sn: string;
  // 是否是输入
  isInput: boolean;
  // 是否禁用反馈设备选项
  isDisableDevice: boolean;
  // 设备列表
  deviceList: InfoSelectOption[];
  // 绑定设备列表
  bindDeviceList: Record<string, any>[];
  // 是否显示回复消息红点
  isReply: boolean;
}

/**
 * 填写反馈页
 */
class Component extends StoreComponent<Props, States> {
  state: States = {
    content: '',
    textLength: 0,
    imageList: [],
    isType: false,
    typeCode: '',
    desc: null,
    typeList: [],
    ButtonLoading: false,
    isOpenDevice: false,
    device: '',
    sn: '',
    isInput: true,
    isDisableDevice: false,
    deviceList: [],
    bindDeviceList: [],
    isReply: false,
  };

  // 链接中的参数
  private urlParams = getUrlParams();
  // input 实例
  private textInput: any;

  constructor(props: Props) {
    super(props, { languageKey: 'page.feedback', titleKey: 'title' });
    this.textInput = React.createRef();
  }
  componentDidMount() {
    this.getType();
    // 链接中带了设备参数{
    if (this.urlParams.typecode) {
      this.setState({ typeCode: this.urlParams.typecode || '' });
      new HttpStateLine(new (Api.feedback.GetContentByCode)({
        code: this.urlParams.typecode,
      }), {
        onSuccess: (stateLine) => {
          this.setState({
            desc: stateLine.result.data?.content,
          });
        },
      }).run();
    }
    if (this.urlParams.deviceName && this.urlParams.pid) {
      this.setState({ device: this.urlParams.deviceName, sn: this.urlParams.pid, isDisableDevice: true });
    } else {
      // 获取设备列表
      this.getDeviceList();
      // 获取绑定用户列表
      this.getBindDeviceList();
    }
  }
  /**
   * 获取问题类型
   */
  private getType = async (): Promise<void> => {
    const httpStateLine = await new HttpStateLine(new Api.feedback.QueryFeedbackType({
    })).run().catch(() => null);
    this.setState({
      typeList: httpStateLine?.result?.data || [],
    });
  };

  /**
   * 获取设备列表
   */
  private getDeviceList = async (): Promise<void> => {
    const httpStateLine = await new HttpStateLine(new Api.feedback.GetDeviceList()).run().catch(() => null);
    this.setState({
      deviceList: httpStateLine?.result?.data?.map((item: Record<string, any>) => ({
        label: item.spuName,
        value: item.spuName,
      })) || [],
    });
  };

  /**
   * 获取当前账户绑定设备列表
   */
  private getBindDeviceList = async (): Promise<void> => {
    const httpStateLine = await new HttpStateLine(new Api.feedback.GetBindDeviceList({ openId: this.storeState.userCenter?.userInfo?.openId })).run().catch(() => null);
    const bindDeviceList = httpStateLine?.result?.data || [];
    this.setState({
      bindDeviceList,
      isInput: bindDeviceList.length === 0,
      device: bindDeviceList[0]?.productName,
      sn: bindDeviceList[0]?.sn,
    });
  };

  /**
   * 获取来源渠道
   * 来源 1-APP设置页面 2-app扫描大屏二维码   11-微信扫描大屏二维码 12-care+ 13-商城小程序  14-H5其它方式打开 21-帖子反馈
   */
  private getFrom = (): string => {
    const from = getUrlParams()?.from;
    switch (from) {
      case 'tv':
        return App.isSuperApp() ? '2' : isWechat() ? '11' : '14';
      case 'care+':
        return '12';
      default:
        return '14';
    }
  };

  /**
   * 提交反馈
   */
  private addFeedback = () => {
    if (this.state.typeCode?.length === 0) {
      this.storeState.common.toast.current?.show({ content: '请选择问题类型' });
    } else if (this.state.textLength === 0) {
      this.storeState.common.toast.current?.show({ content: '请填写详细描述' });
    }
    else if (this.state.textLength < 5) {
      this.storeState.common.toast.current?.show({ content: '至少填写5个字' });
    } else {
      this.setState({ ButtonLoading: true });
      const imageList: { url: string; sortValue: string; }[] = [];
      if (this.state.imageList.length > 0) {
        for (let i = 0; i < this.state.imageList.length; i++) {
          imageList.push({
            url: this.state.imageList[i],
            sortValue: `${i}`,
          });
        }
      }
      new HttpStateLine(new (Api.feedback.AddFeedback)({
        typeCode: this.state.typeCode,
        uid: this.storeState.userCenter.userInfo?.uid,
        content: this.state.content,
        imageList,
        subSource: this.getFrom(),
        device: this.state.device,
        sn: this.state.sn,
      }), {
        onSuccess: () => {
          this.history.push({ pathname: '/feedback/success' });
        },
        // 覆盖错误提示
        onFail: (stateLine) => {
          this.storeState.common.toast.current?.show({ content: `${stateLine.result.message}` });
        },
        onFinish: () => this.setState({ ButtonLoading: false }),
      }).run();
    }
  };

  /**
   * 点击页面其余区域收起移动端键盘
   */
  private bindHandleScroll = () => {
    this.textInput.current.blur();
  };

  /**
   * 获取历史列表
   */
  private getRecord = async (): Promise<void> => {
    const httpStateLine = await new HttpStateLine(new Api.feedback.GetRecord({
      uid: this.storeState.userCenter.userInfo?.uid,
      currentPage: 1,
    })).run().catch(() => null);
    if (httpStateLine) {
      const historyList = httpStateLine?.result?.data.data;
      let isReply = false;
      historyList?.forEach((item) => {
        if (item.replyList.length > 0) isReply = true;
      });
      this.setState({ isReply });
    }
  };

  render(): React.ReactNode {
    return (
      <Container className={s.container} isMobile={true}>
        <div className={s.feedContainer} onTouchMove={this.bindHandleScroll}>
          {/* 反馈设备 */}
          <div className={s.feedType}>
            <div className={this.getClassName([s.typeLabel, s.titleDark])}>反馈设备</div>
            <div
              className={this.state.isDisableDevice ? s.selectButton : ((this.state.device && this.state.sn) ? s.descButton : s.selectButton)}
              onClick={() => {
                // TV端上报参数不允许修改
                if (this.state.isDisableDevice) return;
                this.setState({ isOpenDevice: true });
              }}
            >
              <div className={s.device}>{((this.state.device && this.state.sn)) ? `${this.state.device}-${this.state.sn}` : this.state.sn ? this.state.sn : '选择反馈设备'}</div>
              <img src={images.next} alt=">" width={16} height={16}/>
            </div>
          </div>
          {/* 问题类型 */}
          <div className={s.feedType}>
            <div className={this.getClassName([s.typeLabel, s.titleDark])}>{this.t('type')}</div>
            <div className={this.state.desc ? s.descButton : s.selectButton} onClick={() => this.setState({ isType: true })}>
              <div className={s.device}>{this.state.desc ? `${this.state.desc}` : this.t('pickType')}</div>
              <img src={images.next} alt=">" width={16} height={16}/>
            </div>
          </div>
          {/* 问题描述 */}
          <div className={s.card}>
            {/* 详情描述 */}
            <div className={s.describeTitle}>
              <div className={this.getClassName([s.typeLabel, s.titleDark])}>
                {this.t('detailDescribe')}
              </div>
            </div>
            {/* 详情输入 */}
            <div className={s.descInput}>
              <Input
                placeholder={this.state.desc === '产品建议' ? '请写下您的宝贵意见，以便我们做的更好' : '请详细描述您发生问题时的操作步骤，提供有效截图或视频，以便我们提供更好的帮助。'}
                onChange={(value) => this.setState({ textLength: value.length, content: value })}
                maxSize={500}
                inputRef={this.textInput}
              />
            </div>
            {/* 字数统计 */}
            <div className={this.getClassName([s.textLength, this.state.textLength >= 500 && s.lengthError])}>
              {`${this.state.textLength}/500`}
            </div>
            {/* 图片上传 */}
            <Upload
              maxLimit={3}
              type={['Image', 'Video']}
              defaultValue={this.state.imageList}
              onUpdate={(data) => this.setState({ imageList: data })}
            />
            <div className={s.tip}>
              上传图片/视频（{this.state.imageList.length}/3）
            </div>
          </div>
        </div>
        {/* 提交按钮 */}
        <div className={s.buttonArea}>
          <Button
            color={'#000004'}
            className={s.button}
            disableElevation={true}
            isLoading={this.state.ButtonLoading}
            onClick={() => this.addFeedback()}
            // TODO:
            // disabled={this.state.typeCode.length === 0 || this.state.textLength === 0}
          >
            {this.t('submit')}
          </Button>
          {/* 历史记录入口 */}
          <div className={s.history}>
            <Link to={`/feedback/history${window.location.search}`}>{this.t('gotoHistory')}</Link>
          </div>
        </div>
        {/* 选择反馈类型 */}
        <Type
          open={this.state.isType}
          onUpdate={(typeCode, desc) => this.setState({ typeCode, desc })}
          onClose={() => this.setState({ isType: false })}
          data={this.state.typeList}
        />

        {/* 选择反馈设备 */}
        <Device
          device={this.state.device}
          sn={this.state.sn}
          isInput={this.state.isInput}
          deviceList={this.state.deviceList}
          bindDeviceList={this.state.bindDeviceList}
          open={this.state.isOpenDevice}
          onClose={() => this.setState({ isOpenDevice: false })}
          onConfirm={(device, sn, isInput) => this.setState({ isOpenDevice: false, device, sn, isInput })}
        />
      </Container>
    );
  }
}

export default connect(mapStateToProps)(Component);
